import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import AuthService from '../../services/authService';
import Button from '../common/Button';
import './ChangePassword.css';

const ChangePassword = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push(`Mínimo ${minLength} caracteres`);
    }
    if (!hasUpperCase) {
      errors.push('Al menos una letra mayúscula');
    }
    if (!hasLowerCase) {
      errors.push('Al menos una letra minúscula');
    }
    if (!hasNumbers) {
      errors.push('Al menos un número');
    }
    if (!hasSpecialChar) {
      errors.push('Al menos un carácter especial');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Validate form data
      if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
        throw new Error('Todos los campos son obligatorios');
      }

      if (formData.newPassword !== formData.confirmPassword) {
        throw new Error('Las contraseñas nuevas no coinciden');
      }

      if (formData.currentPassword === formData.newPassword) {
        throw new Error('La nueva contraseña debe ser diferente a la actual');
      }

      // Validate new password strength
      const passwordErrors = validatePassword(formData.newPassword);
      if (passwordErrors.length > 0) {
        throw new Error(`La contraseña debe cumplir: ${passwordErrors.join(', ')}`);
      }

      // Here you would typically call a Cognito service to change the password
      await AuthService.changePassword(
        formData.currentPassword,
        formData.newPassword,
        auth
      );

      setMessage({
        type: 'success',
        text: 'Contraseña actualizada exitosamente'
      });

      // Clear form after successful change
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Redirect back to profile after a short delay
      setTimeout(() => {
        navigate('/settings/profile');
      }, 2000);

    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Error al cambiar la contraseña. Inténtalo de nuevo.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings/profile');
  };

  const getPasswordStrengthIndicator = () => {
    if (!formData.newPassword) return null;
    
    const errors = validatePassword(formData.newPassword);
    const strength = 5 - errors.length;
    const strengthLevels = ['Muy débil', 'Débil', 'Regular', 'Buena', 'Fuerte'];
    const strengthColors = ['#e53e3e', '#fd7f6f', '#fed7aa', '#9ae6b4', '#68d391'];
    
    return (
      <div className="password-strength">
        <div className="strength-bar">
          <div 
            className="strength-fill"
            style={{ 
              width: `${(strength / 5) * 100}%`,
              backgroundColor: strengthColors[strength - 1] || '#e53e3e'
            }}
          />
        </div>
        <span className="strength-text" style={{ color: strengthColors[strength - 1] || '#e53e3e' }}>
          {strengthLevels[strength - 1] || 'Muy débil'}
        </span>
        {errors.length > 0 && (
          <ul className="strength-requirements">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="change-password-container">
      <div className="change-password-header">
        <h1>Cambiar Contraseña</h1>
        <p>Actualiza tu contraseña para mantener tu cuenta segura</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="change-password-card">
        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Contraseña Actual *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('current')}
                aria-label={showPasswords.current ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                <i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
                aria-label={showPasswords.new ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            {getPasswordStrengthIndicator()}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nueva Contraseña *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
                aria-label={showPasswords.confirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <div className="password-mismatch">
                Las contraseñas no coinciden
              </div>
            )}
          </div>

          <div className="password-requirements">
            <h4>Requisitos de la contraseña:</h4>
            <ul>
              <li>Mínimo 8 caracteres</li>
              <li>Al menos una letra mayúscula</li>
              <li>Al menos una letra minúscula</li>
              <li>Al menos un número</li>
              <li>Al menos un carácter especial (!@#$%^&*)</li>
            </ul>
          </div>

          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              icon="fas fa-key"
              disabled={!formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
            >
              {isLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
